import {ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ShareClassService} from './share-class.service';
import {ClientShareClassHolder} from '@shared/interfaces/clientShareClassHolder';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {ShareClassConverterService} from './view-models/share-class-converter.service';
import {ShareClassViewModel} from './view-models/share-class.view-model';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {Router} from '@angular/router';
import {APP_ENTITY_INFORMATION} from '../../../config/app-urls';
import {Keys} from '@shared/keys';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {ShareClassesCardComponent} from './share-classes-card/share-classes-card.component';

@Component({
    selector: 'app-share-classes',
    templateUrl: './share-classes.component.html',
    styleUrls: ['./share-classes.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TableTemplateComponent, ShareClassesCardComponent]
})
export class ShareClassesComponent implements OnInit {

  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly shareClassService = inject(ShareClassService);
  private readonly converter = inject(ShareClassConverterService);
  private readonly clientsideDataService = inject(ClientsideDataService<ShareClassViewModel>);
  private readonly destroyRef = inject(DestroyRef);

  private readonly SEARCH_FIELDS: Keys<ShareClassViewModel> = ['shareClass', 'description'];

  readonly clientCode = input.required<string>();

  readonly clientShareClassClicked = output<ClientShareClassHolder>();

  readonly dataLoading = signal(true);
  readonly dataAvailable = signal(false);
  readonly dataEmpty = signal(false);
  readonly columns = signal<TableTemplateColumnConfig[]>([]);
  readonly sortField = signal('shareClass');
  readonly sortOrder = signal(1);
  readonly searchString = signal('');
  readonly pageNumber = signal(1);
  readonly pageSize = signal(20);
  readonly currentPage = signal<PageViewModel<ShareClassViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  ngOnInit(): void {
    this.loadData(this.clientCode());
    this.loadSavedSort();
    this.populateColumns();
  }

  private loadData(clientCode: string): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.shareClassService.getShareClassesForClient(clientCode).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(shareClasses => {
        if (shareClasses.length === 0) {
          this.router.navigate(APP_ENTITY_INFORMATION.toRouterLinkCommands(clientCode));
          return;
        }

        const shareClassViewModels = this.converter.convert(shareClasses);
        this.clientsideDataService.initialize(shareClassViewModels);
        this.updateData();
        this.dataLoading.set(false);
        this.dataEmpty.set(shareClassViewModels.length === 0);
        this.dataAvailable.set(!this.dataEmpty());
      });
  }

  private loadSavedSort() {
    const storedSort = this.sessionStorageService.getSort(SortedTable.ShareClasses);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }
  }

  private updateData(): void {
    this.currentPage.set(this.clientsideDataService.generatePage(this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()));
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }

    this.sessionStorageService.setSort(SortedTable.ShareClasses, this.sortField(), this.sortOrder());
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
    const vm = dataViewModel as ShareClassViewModel;

    const clientShareClassHolder: ClientShareClassHolder = {
      clientCode: this.clientCode(),
      shareClassLineNo: vm.shareClassLineNo,
      shareClass: vm.shareClass,
      nominalValue: vm.nominalValue,
      currencyCode: vm.currencyCode,
    };

    this.clientShareClassClicked.emit(clientShareClassHolder);
  }

  populateColumns() {
    this.columns.set([
      {
        title: 'Class',
        dataProperty: 'shareClass',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '12%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Description',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '14%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Effective Date',
        dataProperty: 'effectiveDate',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '10%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Currency',
        dataProperty: 'currencyCode',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '8%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Par Value',
        dataProperty: 'nominalValue',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '8%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Shares Authorised',
        dataProperty: 'shares',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Authorised Value',
        dataProperty: 'valueAuthorised',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Shares Issued',
        dataProperty: 'sharesIssued',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Shares Available',
        dataProperty: 'sharesAvailable',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
    ]);
  }
}
